import { useRef } from 'react';

import { createPortal } from 'react-dom';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import styled, { createGlobalStyle } from 'styled-components';

import { PagePopupHeader } from './PagePopupHeader';
import { PagePopupProps } from './types';

const GlobalStyle = createGlobalStyle`
    html {
        overflow: hidden;
    }
`;

export const PagePopup = (props: PagePopupProps) => {
  const { t } = useTranslation('global');
  const {
    visible,
    children,
    title,
    position = 'right',
    header = true,
    disableAnimation,
    okText = t('global:m_button_done'),
    closeIcon = 'back',
    background,
    onClose,
    onOk,
    onExited,
  } = props;
  const navigate = useNavigate();

  const handleClose = () => {
    if (!onClose) {
      navigate(-1);
      return;
    }
    onClose();
  };

  const root = document.getElementById('root') as HTMLElement;
  const nodeRef = useRef(null);

  return createPortal(
    <CSSTransition
      nodeRef={nodeRef}
      in={visible}
      timeout={disableAnimation ? 0 : 400}
      classNames={disableAnimation ? '' : 'page-popup'}
      unmountOnExit
      onExited={onExited}
    >
      <Wrapper ref={nodeRef} position={position || 'right'}>
        <PopupDialog
          style={{
            background: background === 'gray' ? '#F6F7F9' : '',
          }}
        >
          <GlobalStyle />
          {!!header && !!title && (
            <PagePopupHeader
              title={title}
              okText={okText}
              closeIcon={closeIcon}
              onOk={onOk}
              onClose={handleClose}
            />
          )}
          {children}
        </PopupDialog>
        <PopupMask />
      </Wrapper>
    </CSSTransition>,
    root,
  );
};

const PopupMask = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 0;
  width: 100vw;
  height: 100dvh;
  background-color: rgba(0, 0, 0, 0.5);
`;
const PopupDialog = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1;
  width: 100vw;
  height: 100dvh;
  padding-top: env(safe-area-inset-top);
  padding-bottom: env(safe-area-inset-bottom);
  background: white;
  overflow: hidden overlay;
`;

const getHiddenTrasformStyle = (position: PagePopupProps['position']) => {
  switch (position) {
    case 'top':
      return `transform: translateY(-100%);`;
    case 'bottom':
      return `transform: translateY(100%);`;
    case 'left':
      return `transform: translateX(-100%);`;
    case 'right':
    default:
      return `transform: translateX(100%);`;
  }
};

type PopupDialogProps = Pick<PagePopupProps, 'position'>;

const Wrapper = styled.div<PopupDialogProps>`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;

  &.page-popup-enter {
    ${PopupMask} {
      opacity: 0;
      will-change: opacity;
    }
    ${PopupDialog} {
      ${(p) => getHiddenTrasformStyle(p.position)}
      opacity: 0;
      will-change: opacity transform;
    }
  }
  &.page-popup-enter-active {
    ${PopupMask} {
      opacity: 1;
      transition: opacity 400ms ease;
      will-change: opacity;
    }
    ${PopupDialog} {
      transform: translate(0, 0);
      opacity: 1;
      transition:
        transform 400ms cubic-bezier(0.215, 0.61, 0.355, 1),
        opacity 100ms cubic-bezier(0.215, 0.61, 0.355, 1);
      will-change: opacity transform;
    }
  }
  &.page-popup-exit {
    ${PopupMask} {
      opacity: 1;
      will-change: opacity;
    }
    ${PopupDialog} {
      transform: translate(0, 0);
      will-change: transform;
    }
  }
  &.page-popup-exit-active {
    ${PopupMask} {
      opacity: 0;
      transition: opacity 400ms ease;
      will-change: opacity;
    }
    ${PopupDialog} {
      ${(p) => getHiddenTrasformStyle(p.position)}
      transition: transform 400ms cubic-bezier(0.215, 0.61, 0.355, 1);
      will-change: transform;
    }
  }
`;
